import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Button } from '@src/components/ui/button/button';
import { cn } from '@src/utils/ui';
import { Checkbox } from '@src/components/ui/react-hook-form/checkbox/checkbox';
import { PhoneInput } from '@src/components/common/phone-input/phone-input';
import { Form } from '@src/components/ui/react-hook-form/form/form';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useSubscribeToSms } from './api/users';

const smsSchema = z.object({
    smsConsent: z.boolean(),
    phoneNumber: z
        .string({ required_error: 'Field cannot be empty' })
        .min(1, { message: 'Field cannot be empty' })
        .regex(/^[2-9]\d{2}[2-9]\d{2}\d{4}$/, 'Please enter a valid phone number')
});

export const SmsSubscription = ({ onClose }) => {
    const { mutate: subscribeToSms } = useSubscribeToSms();
    const handleAccept = data => {
        const phoneNumberWithCountyCode = `+1${data.phoneNumber.replace(/ /g, '')}`;
        subscribeToSms(
            {
                smsConsent: data.smsConsent,
                phoneNumber: phoneNumberWithCountyCode
            },
            {
                onSuccess: () => {
                    onClose();
                },
                onError: error => {
                    console.error('Error sms subscription:', error);
                }
            }
        );
    };
    return (
        <Dialog open aria-labelledby='alert-dialog-title' aria-describedby='alert-dialog-description'>
            <DialogContent className='tw-max-w-[400px]'>
                <Form
                    onSubmit={handleAccept}
                    validator={{ schema: smsSchema }}
                    defaultValues={{ smsConsent: true, phoneNumber: '' }}
                >
                    <SmsSubscriptionFormContent onClose={onClose} />
                </Form>
            </DialogContent>
        </Dialog>
    );
};

const SmsSubscriptionFormContent = ({ onClose }) => {
    const { watch, setValue } = useFormContext();
    const isChecked = watch('smsConsent');
    const phoneNumber = watch('phoneNumber');
    const handleInputChange = val => {
        setValue('phoneNumber', val, {
            shouldValidate: val.replace(/\s/g, '').length === 10
        });
    };

    return (
        <div className='tw-flex tw-flex-col tw-gap-[16px] tw-text-bcaa-grey-900 tw-text-[16px]'>
            <h6 className='tw-font-medium tw-text-[20px]'>Stay in the loop with SMS alerts</h6>

            <p>Get important updates instantly - no need to check your email!</p>
            <p>You can also manage SMS alerts in your account profile.</p>

            {isChecked && <PhoneInput name='phoneNumber' value={phoneNumber} onChange={handleInputChange} isRHF />}

            <div className='tw-flex tw-gap-[8px] tw-items-start'>
                <Checkbox
                    className='!tw-items-start'
                    checkBoxProps={{ size: 'small', defaultChecked: true }}
                    name='smsConsent'
                    value
                    label='I agree to receive SMS messages about my account and transactions.*'
                    onChange={value => setValue('smsConsent', value)}
                />
            </div>
            <div className='tw-flex tw-flex-col tw-gap-[8px]'>
                <Button
                    className={cn('tw-p-[8px] tw-text-[14px] tw-w-full tw-uppercase')}
                    variant='contained'
                    loadingPosition='start'
                    disabled={!isChecked}
                    type='submit'
                >
                    Next
                </Button>
                {!isChecked && (
                    <Button
                        className={cn('tw-p-[8px] tw-text-[14px] tw-w-full tw-uppercase')}
                        variant='text'
                        loadingPosition='start'
                        onClick={onClose}
                    >
                        Skip
                    </Button>
                )}
            </div>

            <p className='tw-text-[12px] tw-text-bcaa-grey-700'>
                *Standard message and data rates may apply. You can opt out anytime in your User Profile.
            </p>
        </div>
    );
};

SmsSubscription.propTypes = {
    onClose: PropTypes.func.isRequired
};

SmsSubscriptionFormContent.propTypes = {
    onClose: PropTypes.func.isRequired
};
